import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Card, Form, InputGroup, Collapse, Spinner, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { getPillarWeights, getScoringSubscores, getScoringPillars, updatePillarWeights, updateScoringSubscores, updateScoringPillars, getPolicyThreshold, updatePolicyThreshold, getPolicyAggregationStrategy, updatePolicyAggregationStrategy } from "../context/functions/AdminFunction";

const ScoreConfigManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [pillarWeights, setPillarWeights] = useState([]);
  const [subScores, setSubScores] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [threshold, setThreshold] = useState({ MIN: 0, MAX: 100 });
  const [aggregationStrategy, setAggregationStrategy] = useState("MIN");
  
  const [originalData, setOriginalData] = useState({
    pillarWeights: [],
    subScores: [],
    pillars: [],
    threshold: { MIN: 0, MAX: 100 },
    aggregationStrategy: "MIN"
  });

  const [expandedSubScores, setExpandedSubScores] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [weightsRes, subscoresRes, pillarsRes, thresholdRes, aggregationRes] = await Promise.all([
        getPillarWeights(),
        getScoringSubscores(),
        getScoringPillars(),
        getPolicyThreshold(),
        getPolicyAggregationStrategy()
      ]);

      if (weightsRes?.status === "success" && weightsRes?.data?.pillarWeights) {
        const weights = weightsRes.data.pillarWeights;
        setPillarWeights(weights);
        setOriginalData(prev => ({ ...prev, pillarWeights: JSON.parse(JSON.stringify(weights)) }));
      }

      if (subscoresRes?.status === "success" && subscoresRes?.data?.subScores) {
        const scores = subscoresRes.data.subScores.map(subScore => ({
          ...subScore,
          options: subScore.options?.map(option => ({
            ...option,
            label: option.label ? decodeHTMLEntities(option.label) : option.label
          }))
        }));
        setSubScores(scores);
        setOriginalData(prev => ({ ...prev, subScores: JSON.parse(JSON.stringify(scores)) }));
      }

      if (pillarsRes?.status === "success" && pillarsRes?.data?.pillars) {
        const pillarData = pillarsRes.data.pillars;
        setPillars(pillarData);
        setOriginalData(prev => ({ ...prev, pillars: JSON.parse(JSON.stringify(pillarData)) }));
      }

      if (thresholdRes?.status === "success" && thresholdRes?.data?.threshold) {
        const thresholdData = thresholdRes.data.threshold;
        setThreshold(thresholdData);
        setOriginalData(prev => ({ ...prev, threshold: JSON.parse(JSON.stringify(thresholdData)) }));
      }

      if (aggregationRes?.status === "success" && aggregationRes?.data?.aggregationStrategy) {
        const strategy = aggregationRes.data.aggregationStrategy;
        setAggregationStrategy(strategy);
        setOriginalData(prev => ({ ...prev, aggregationStrategy: strategy }));
      }

      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching score config:", error);
      toast.error("Failed to load score configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDiscardChanges = () => {
    setPillarWeights(JSON.parse(JSON.stringify(originalData.pillarWeights)));
    setSubScores(JSON.parse(JSON.stringify(originalData.subScores)));
    setPillars(JSON.parse(JSON.stringify(originalData.pillars)));
    setThreshold(JSON.parse(JSON.stringify(originalData.threshold)));
    setAggregationStrategy(originalData.aggregationStrategy);
    setHasChanges(false);
    toast.info("Changes discarded");
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const updates = [];

      if (JSON.stringify(pillarWeights) !== JSON.stringify(originalData.pillarWeights)) {
        updates.push(updatePillarWeights(pillarWeights));
      }

        if (JSON.stringify(subScores) !== JSON.stringify(originalData.subScores)) {
          const changedSubScores = subScores.filter((subScore, index) => {
            const original = originalData.subScores[index];
            return JSON.stringify(subScore) !== JSON.stringify(original);
          }).map(subScore => {
            const update = { subScoreKey: subScore.subScoreKey };
            
            if (subScore.frozenValue !== undefined && subScore.frozenValue !== null) {
              update.frozenValue = subScore.frozenValue;
            }
            
            if (subScore.options && subScore.options.length > 0) {
              update.options = subScore.options.map(option => ({
                ...option,
                label: option.label || ""
              }));
            }
            
            return update;
          });
          console.log("Saving changedSubScores:", JSON.stringify(changedSubScores, null, 2));

        if (changedSubScores.length > 0) {
          updates.push(updateScoringSubscores(changedSubScores));
        }
      }

      if (JSON.stringify(pillars) !== JSON.stringify(originalData.pillars)) {
        const changedPillars = pillars.filter((pillar, index) => {
          const original = originalData.pillars[index];
          return JSON.stringify(pillar) !== JSON.stringify(original);
        });

        if (changedPillars.length > 0) {
          updates.push(updateScoringPillars(changedPillars));
        }
      }

      if (JSON.stringify(threshold) !== JSON.stringify(originalData.threshold)) {
        if (threshold.MAX < threshold.MIN) {
          toast.error("MAX must be greater than or equal to MIN");
          setSaving(false);
          return;
        }
        updates.push(updatePolicyThreshold(threshold));
      }

      if (aggregationStrategy !== originalData.aggregationStrategy) {
        updates.push(updatePolicyAggregationStrategy(aggregationStrategy));
      }

      if (updates.length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }

      await Promise.all(updates);
      await fetchData();
      toast.success("Score configuration saved successfully");
    } catch (error) {
      console.error("Error saving score config:", error);
      toast.error(error.response?.data?.message || "Failed to save score configuration");
    } finally {
      setSaving(false);
    }
  };

  const handlePillarWeightChange = (index, value) => {
    const newWeights = [...pillarWeights];
    newWeights[index].weight = parseFloat(value);
    setPillarWeights(newWeights);
    setHasChanges(true);
  };

  const handleSubScoreOptionChange = (subScoreIndex, optionIndex, field, value) => {
    const newSubScores = [...subScores];
    const newValue = field === "score" || field === "min" || field === "max" ? parseFloat(value) : decodeHTMLEntities(value);
    newSubScores[subScoreIndex].options[optionIndex][field] = newValue;
    setSubScores(newSubScores);
    setHasChanges(true);
  };

  const handleFrozenValueChange = (subScoreIndex, value) => {
    const newSubScores = [...subScores];
    newSubScores[subScoreIndex].frozenValue = parseFloat(value);
    setSubScores(newSubScores);
    setHasChanges(true);
  };

  const handlePillarMappingWeightChange = (pillarIndex, mappingIndex, value) => {
    const newPillars = [...pillars];
    newPillars[pillarIndex].mappings[mappingIndex].weight = parseFloat(value);
    setPillars(newPillars);
    setHasChanges(true);
  };

  const handleThresholdChange = (field, value) => {
    const newThreshold = { ...threshold };
    newThreshold[field] = parseFloat(value);
    setThreshold(newThreshold);
    setHasChanges(true);
  };

  const handleAggregationStrategyChange = (value) => {
    setAggregationStrategy(value);
    setHasChanges(true);
  };

  const toggleSubScoreExpansion = (subScoreKey) => {
    setExpandedSubScores(prev => ({
      ...prev,
      [subScoreKey]: !prev[subScoreKey]
    }));
  };

  const calculatePillarWeightsSum = () => {
    return pillarWeights.reduce((sum, pw) => sum + pw.weight, 0);
  };

  const calculatePillarMappingSum = (pillarIndex) => {
    return pillars[pillarIndex]?.mappings?.reduce((sum, m) => sum + m.weight, 0) || 0;
  };

  const decodeHTMLEntities = (text) => {
    if (!text) return text;
    const textArea = document.createElement("textarea");
    textArea.innerHTML = text;
    return textArea.value;
  };

  const encodeHTMLEntities = (text) => {
    if (!text) return text;
    return text.replace(/&/g, "&amp;")
               .replace(/</g, "&lt;")
               .replace(/>/g, "&gt;")
               .replace(/"/g, "&quot;")
               .replace(/'/g, "&#39;");
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="score-config-management">
      <div className="header_table d-flex justify-content-between align-items-center mb-4 px-3" style={{ position: "sticky", top: "0", backgroundColor: "#ffffff", zIndex: 1000, padding: "20px 0", borderBottom: "1px solid #e0e0e0" }}>
        <h1 className="mb-0">Score Configuration Management</h1>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-secondary" 
            onClick={handleDiscardChanges}
            disabled={!hasChanges || saving}
          >
            Discard Changes
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveChanges}
            disabled={!hasChanges || saving}
            style={{ backgroundColor: "#32493C", borderColor: "#32493C" }}
          >
            {saving ? <Spinner animation="border" size="sm" /> : "Save Changes"}
          </Button>
        </div>
      </div>

      {calculatePillarWeightsSum() !== 1.0 && (
        <Alert variant="warning" className="mb-3">
          Pillar weights must sum to 1.0. Current sum: {calculatePillarWeightsSum().toFixed(2)}
        </Alert>
      )}

      {threshold.MAX < threshold.MIN && (
        <Alert variant="danger" className="mb-3">
          MAX must be greater than or equal to MIN
        </Alert>
      )}

      <div className="config-sections">
        <Card className="mb-4">
          <Card.Header style={{ backgroundColor: "#32493C", color: "#ffffff", border: "none" }}>
            <h4 className="mb-0" style={{ color: "#ffffff" }}>Policy Threshold</h4>
          </Card.Header>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>MIN</th>
                  <th>MAX</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={threshold.MIN}
                      onChange={(e) => handleThresholdChange("MIN", e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={threshold.MAX}
                      onChange={(e) => handleThresholdChange("MAX", e.target.value)}
                    />
                  </td>
                </tr>
              </tbody>
              </Table>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header style={{ backgroundColor: "#32493C", color: "#ffffff", border: "none" }}>
            <h4 className="mb-0" style={{ color: "#ffffff" }}>Policy Aggregation Strategy</h4>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-3">This configuration applies when multiple tenants are selected for risk assessment report.</p>
            <Form.Group>
              <Form.Label>Aggregation Strategy</Form.Label>
              <Form.Select
                value={aggregationStrategy}
                onChange={(e) => handleAggregationStrategyChange(e.target.value)}
              >
                <option value="MIN">MIN</option>
                <option value="MAX">MAX</option>
                <option value="MEAN">MEAN</option>
              </Form.Select>
            </Form.Group>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header style={{ backgroundColor: "#32493C", color: "#ffffff", border: "none" }}>
            <h4 className="mb-0" style={{ color: "#ffffff" }}>Pillar Weights</h4>
          </Card.Header>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Pillar Key</th>
                  <th>Weight</th>
                </tr>
              </thead>
              <tbody>
                {pillarWeights.map((pw, index) => (
                  <tr key={pw.pillarKey}>
                    <td>{pw.pillarKey}</td>
                    <td>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        value={pw.weight}
                        onChange={(e) => handlePillarWeightChange(index, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
                <tr className="table-secondary">
                  <td><strong>Total</strong></td>
                  <td><strong>{calculatePillarWeightsSum().toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header style={{ backgroundColor: "#32493C", color: "#ffffff", border: "none" }}>
            <h4 className="mb-0" style={{ color: "#ffffff" }}>Sub-Scores</h4>
          </Card.Header>
          <Card.Body>
            {subScores.map((subScore, subScoreIndex) => (
              <Card key={subScore.subScoreKey} className="mb-3">
                 <Card.Header 
                   className="d-flex justify-content-between align-items-center"
                   style={{ cursor: "pointer", backgroundColor: "#ECECEC", border: "none", color: "#000" }}
                   onClick={() => toggleSubScoreExpansion(subScore.subScoreKey)}
                 >
                  <h5 className="mb-0">{subScore.subScoreKey}</h5>
                  <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>{expandedSubScores[subScore.subScoreKey] ? "−" : "+"}</span>
                </Card.Header>
                <Collapse in={expandedSubScores[subScore.subScoreKey]} transition>
                  <Card.Body>
                    {subScore.frozenValue !== null && subScore.frozenValue !== undefined ? (
                      <div className="mb-3">
                        <label className="form-label">Frozen Value:</label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          value={subScore.frozenValue}
                          onChange={(e) => handleFrozenValueChange(subScoreIndex, e.target.value)}
                         />
                       </div>
                     ) : (
                       <>
                         {subScore.options?.some(o => o.min !== undefined || o.max !== undefined) ? (
                           <Table responsive hover size="sm">
                             <thead>
                               <tr>
                                 <th>Label</th>
                                 <th>Min</th>
                                 <th>Max</th>
                                 <th>Score</th>
                               </tr>
                             </thead>
                             <tbody>
                               {subScore.options?.map((option, optionIndex) => (
                                 <tr key={option.label_id || optionIndex}>
                                   <td>
                                     <Form.Control
                                       type="text"
                                       value={option.label || ""}
                                       onChange={(e) => handleSubScoreOptionChange(
                                         subScoreIndex, 
                                         optionIndex, 
                                         "label", 
                                         e.target.value
                                       )}
                                     />
                                   </td>
                                   <td>
                                     {option.min !== undefined ? (
                                       <Form.Control
                                         type="number"
                                         step="0.01"
                                         value={option.min}
                                         onChange={(e) => handleSubScoreOptionChange(
                                           subScoreIndex, 
                                           optionIndex, 
                                           "min", 
                                           e.target.value
                                         )}
                                       />
                                     ) : (
                                       <span>-</span>
                                     )}
                                   </td>
                                   <td>
                                     {option.max !== undefined ? (
                                       <Form.Control
                                         type="number"
                                         step="0.01"
                                         value={option.max}
                                         onChange={(e) => handleSubScoreOptionChange(
                                           subScoreIndex, 
                                           optionIndex, 
                                           "max", 
                                           e.target.value
                                         )}
                                       />
                                     ) : (
                                       <span>-</span>
                                     )}
                                   </td>
                                   <td>
                                     <Form.Control
                                       type="number"
                                       step="1"
                                       min="0"
                                       max="100"
                                       value={option.score}
                                       onChange={(e) => handleSubScoreOptionChange(
                                         subScoreIndex, 
                                         optionIndex, 
                                         "score", 
                                         e.target.value
                                       )}
                                     />
                                   </td>
                                 </tr>
                               ))}
                             </tbody>
                           </Table>
                         ) : (
                           <Table responsive hover size="sm">
                             <thead>
                               <tr>
                                 <th>Label</th>
                                 <th>Score</th>
                               </tr>
                             </thead>
                             <tbody>
                               {subScore.options?.map((option, optionIndex) => (
                                 <tr key={option.label_id || optionIndex}>
                                   <td>{option.label}</td>
                                   <td>
                                     <Form.Control
                                       type="number"
                                       step="1"
                                       min="0"
                                       max="100"
                                       value={option.score}
                                       onChange={(e) => handleSubScoreOptionChange(
                                         subScoreIndex, 
                                         optionIndex, 
                                         "score", 
                                         e.target.value
                                       )}
                                     />
                                   </td>
                                 </tr>
                               ))}
                             </tbody>
                           </Table>
                         )}
                       </>
                     )}
                  </Card.Body>
                </Collapse>
              </Card>
            ))}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header style={{ backgroundColor: "#32493C", color: "#ffffff", border: "none" }}>
            <h4 className="mb-0" style={{ color: "#ffffff" }}>Pillar Mappings</h4>
          </Card.Header>
          <Card.Body>
            {pillars.map((pillar, pillarIndex) => (
              <div key={pillar.pillarKey} className="mb-4">
                <h5 className="mb-3">
                  {pillar.pillarKey}
                  {calculatePillarMappingSum(pillarIndex) !== 1.0 && (
                    <span className="text-danger ms-2">
                      (Sum: {calculatePillarMappingSum(pillarIndex).toFixed(2)} - must be 1.0)
                    </span>
                  )}
                </h5>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Sub-Score Key</th>
                      <th>Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pillar.mappings?.map((mapping, mappingIndex) => (
                      <tr key={mapping.subScoreKey}>
                        <td>{mapping.subScoreKey}</td>
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            max="1"
                            value={mapping.weight}
                            onChange={(e) => handlePillarMappingWeightChange(
                              pillarIndex, 
                              mappingIndex, 
                              e.target.value
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="table-secondary">
                      <td><strong>Total</strong></td>
                      <td><strong>{calculatePillarMappingSum(pillarIndex).toFixed(2)}</strong></td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            ))}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ScoreConfigManagement;